import React, { useEffect, useState } from "react";
import {
  useNotify,
  useRedirect,
  Confirm,
} from "react-admin";
import { Button, CircularProgress, TablePagination } from "@material-ui/core";
import { useTokenOps } from "../../../components/TokenContext";
import { hostname } from "../../../providers/api";
import { gtinCsvRows, naReasonFor, toCsv } from "../gtinList";

const GtinListTab = () => {
  const [gtins, setGtins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredGtins, setFilteredGtins] = useState([]);
  const [filters, setFilters] = useState({ reference: "", gtin: "" });
  const [selectedGtins, setSelectedGtins] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState('asc');
  const [refreshingNaReasons, setRefreshingNaReasons] = useState(false);

  const tokenOps = useTokenOps();
  const notify = useNotify();
  const redirect = useRedirect();

  // Función para formatear fecha ISO a DD/MM/YYYY
  const formatDate = (isoDate) => {
    if (!isoDate) return "-";
    try {
      const date = new Date(isoDate);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return "-";
    }
  };

  const fetchGtins = () => {
    setLoading(true);
    fetch(`${hostname}/ecobalyse/getAllGtin`, {
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((response) => {
        console.log('[GTIN] Response status:', response.status);
        if (!response.ok) {
          throw new Error(`Error fetching GTINs: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        console.log('[GTIN] Response data:', data);
        let gtinArray = Array.isArray(data) ? data : data.data || [];

        // Verificar estructura de datos
        if (gtinArray.length > 0) {
          console.log('[GTIN] Sample record structure:', gtinArray[0]);
          console.log('[GTIN] Has id field?', 'id' in gtinArray[0]);
          console.log('[GTIN] Has articleId field?', 'articleId' in gtinArray[0]);
        }

        // Verificar si todos los registros tienen ID
        const recordsWithoutId = gtinArray.filter(g => !g.id);
        if (recordsWithoutId.length > 0) {
          console.warn('[GTIN] Records without ID field:', recordsWithoutId.length);
          console.warn('[GTIN] Sample record without ID:', recordsWithoutId[0]);
        }

        // IMPORTANTE: Usar el campo id si existe, sino usar articleId como key de React
        // Pero necesitamos el id real para las consultas
        gtinArray = gtinArray.map((gtin, index) => ({
          ...gtin,
          // Para React key y navegación, usar el id real si existe
          id: gtin.id || `temp-${index}`, // Temporal key si no hay id
          _hasRealId: !!gtin.id // Flag para saber si tiene id real
        }));

        console.log('[GTIN] GTINs processed:', gtinArray);
        console.log('[GTIN] Records with real ID:', gtinArray.filter(g => g._hasRealId).length);
        console.log('[GTIN] Records without real ID:', gtinArray.filter(g => !g._hasRealId).length);

        setGtins(gtinArray);
        setFilteredGtins(gtinArray);
      })
      .catch((error) => {
        console.error('[GTIN] Error:', error);
        notify(`Error: ${error.message}`, "error");
        setGtins([]);
        setFilteredGtins([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchGtins();
  }, []);

  useEffect(() => {
    let filtered = gtins;

    if (filters.reference) {
      filtered = filtered.filter((gtin) =>
        gtin.reference?.toLowerCase().includes(filters.reference.toLowerCase())
      );
    }

    if (filters.gtin) {
      filtered = filtered.filter((gtin) =>
        gtin.gtin?.toLowerCase().includes(filters.gtin.toLowerCase())
      );
    }

    setFilteredGtins(filtered);
    setPage(0); // Resetear a la primera página cuando cambian los filtros
  }, [filters, gtins]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const getSortedData = (data) => {
    if (!orderBy) return data;

    return [...data].sort((a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Manejar valores nulos o undefined
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';

      // Convertir a minúsculas si son strings
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Checkbox handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const selectableGtins = filteredGtins
        .filter(gtin => gtin._hasRealId)
        .map(gtin => gtin.id);
      setSelectedGtins(selectableGtins);
    } else {
      setSelectedGtins([]);
    }
  };

  const handleSelectOne = (gtinId) => {
    setSelectedGtins((prev) => {
      if (prev.includes(gtinId)) {
        return prev.filter(id => id !== gtinId);
      } else {
        return [...prev, gtinId];
      }
    });
  };

  const handleClearSelection = () => {
    setSelectedGtins([]);
  };

  const handleExportSelected = () => {
    const selectedData = gtins.filter(gtin => selectedGtins.includes(gtin.id));
    const csvContent = toCsv(gtinCsvRows(selectedData, formatDate));

    // BOM so Excel reads the UTF-8 text (accents, quotes) correctly.
    const blob = new Blob(["﻿", csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `gtins_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notify(`${selectedGtins.length} GTINs exported successfully`, "success");
  };

  // N/A GTINs uploaded before the reason was stored have none; recompute them.
  const handleRefreshNaReasons = () => {
    setRefreshingNaReasons(true);
    fetch(`${hostname}/ecobalyse/backfill-na-reasons`, {
      method: "POST",
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then(async (response) => {
        const body = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(body?.message || `Error ${response.status}`);
        }
        notify(
          `N/A reasons refreshed: ${body.updated} GTINs (${body.articles} articles` +
            `${body.nowEligible ? `, ${body.nowEligible} now eligible: re-upload their GTINs` : ""}` +
            `${body.failed ? `, ${body.failed} could not be checked` : ""}).`,
          body.failed ? "warning" : "success",
        );
        fetchGtins();
      })
      .catch((error) => {
        notify(`Error refreshing N/A reasons: ${error.message}`, "error");
      })
      .finally(() => setRefreshingNaReasons(false));
  };

  const handleDeleteClick = () => {
    setConfirmDeleteOpen(true);
  };

  const handleDeleteConfirm = () => {
    setConfirmDeleteOpen(false);

    // Delete selected GTINs with query parameter
    const idsQuery = selectedGtins.join(',');

    fetch(`${hostname}/account-article-gtin?ids=${idsQuery}`, {
      method: "DELETE",
      headers: {
        authorization: "Bearer " + tokenOps.account?.accessToken,
      },
    })
      .then((response) => {
        if (response.ok) {
          notify(`${selectedGtins.length} GTIN(s) deleted successfully`, "success");
          setSelectedGtins([]);
          fetchGtins();
        } else {
          throw new Error("Deletion failed");
        }
      })
      .catch((error) => {
        console.error('[GTIN] Delete error:', error);
        notify(`Error deleting GTINs: ${error.message}`, "error");
      });
  };

  const handleDeleteCancel = () => {
    setConfirmDeleteOpen(false);
  };

  const selectableGtins = filteredGtins.filter(gtin => gtin._hasRealId);
  const isAllSelected = selectableGtins.length > 0 && selectedGtins.length === selectableGtins.length;
  const isIndeterminate = selectedGtins.length > 0 && selectedGtins.length < selectableGtins.length;

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "400px"
      }}>
        <CircularProgress />
      </div>
    );
  }

  const handleRowClick = (gtin, event) => {
    // Evitar navegación si se hizo clic en el checkbox
    if (event.target.type === 'checkbox') {
      return;
    }

    // Verificar que el GTIN tenga un ID real
    if (!gtin._hasRealId || !gtin.id || gtin.id.startsWith('temp-')) {
      console.error('[GTIN] Cannot navigate - record has no real ID:', gtin);
      notify('This GTIN record has no ID and cannot be viewed. Please contact support.', 'error');
      return;
    }

    // Redirigir a la vista detallada del GTIN usando el ID real
    console.log('[GTIN] Navigating to GTIN with ID:', gtin.id);
    console.log('[GTIN] Full record:', gtin);
    redirect(`/account-article-gtin/${gtin.id}/show`);
  };

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ marginBottom: "15px", display: "flex", gap: "10px", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search Reference"
            value={filters.reference}
            onChange={(e) => handleFilterChange("reference", e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
          <input
            type="text"
            placeholder="Search GTIN"
            value={filters.gtin}
            onChange={(e) => handleFilterChange("gtin", e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
        </div>
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleRefreshNaReasons}
            disabled={refreshingNaReasons}
            title="Recompute why each N/A GTIN is not eligible. Statuses do not change."
          >
            {refreshingNaReasons ? "Refreshing N/A reasons…" : "Refresh N/A reasons"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => redirect('/account-article-gtin/create')}
          >
            Create
          </Button>
        </div>
      </div>

      {/* Selection info bar */}
      {selectedGtins.length > 0 && (
        <div style={{
          backgroundColor: "#e3f2fd",
          border: "1px solid #2196f3",
          borderRadius: "4px",
          padding: "12px 16px",
          marginBottom: "15px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span style={{ fontWeight: 500, color: "#1976d2" }}>
              {selectedGtins.length} row{selectedGtins.length !== 1 ? 's' : ''} selected
            </span>
            <Button
              size="small"
              onClick={handleClearSelection}
              style={{ textTransform: "none", color: "#1976d2" }}
            >
              Clear selection
            </Button>
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleExportSelected}
              style={{
                backgroundColor: "#1976d2",
                color: "white",
                textTransform: "none"
              }}
            >
              Export Selected
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleDeleteClick}
              style={{
                backgroundColor: "#d32f2f",
                color: "white",
                textTransform: "none"
              }}
            >
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {filteredGtins.length === 0 ? (
        <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
          No GTINs found
        </div>
      ) : (
        <div style={{ marginTop: "15px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #ddd" }}>
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                <th style={{ padding: "12px", textAlign: "center", borderBottom: "2px solid #ddd", width: "40px" }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={input => {
                      if (input) {
                        input.indeterminate = isIndeterminate;
                      }
                    }}
                    onChange={handleSelectAll}
                    disabled={selectableGtins.length === 0}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th 
                  onClick={() => handleSort('name')}
                  style={{ 
                    padding: "12px", 
                    textAlign: "left", 
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Name <span style={{ opacity: orderBy === 'name' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'name' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th 
                  onClick={() => handleSort('articleId')}
                  style={{ 
                    padding: "12px", 
                    textAlign: "left", 
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Article ID <span style={{ opacity: orderBy === 'articleId' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'articleId' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th 
                  onClick={() => handleSort('reference')}
                  style={{ 
                    padding: "12px", 
                    textAlign: "left", 
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Reference <span style={{ opacity: orderBy === 'reference' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'reference' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th 
                  onClick={() => handleSort('gtin')}
                  style={{ 
                    padding: "12px", 
                    textAlign: "left", 
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  GTIN <span style={{ opacity: orderBy === 'gtin' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'gtin' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th 
                  onClick={() => handleSort('projectName')}
                  style={{ 
                    padding: "12px", 
                    textAlign: "left", 
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Project <span style={{ opacity: orderBy === 'projectName' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'projectName' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th 
                  onClick={() => handleSort('traceabilityStatus')}
                  style={{ 
                    padding: "12px", 
                    textAlign: "left", 
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Traceability <span style={{ opacity: orderBy === 'traceabilityStatus' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'traceabilityStatus' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th 
                  onClick={() => handleSort('EcobalyseStatus')}
                  style={{ 
                    padding: "12px", 
                    textAlign: "left", 
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Score <span style={{ opacity: orderBy === 'EcobalyseStatus' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'EcobalyseStatus' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th
                  onClick={() => handleSort('ecobalyseNaReason')}
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  N/A reason <span style={{ opacity: orderBy === 'ecobalyseNaReason' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'ecobalyseNaReason' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th 
                  onClick={() => handleSort('lastEcobalyseAt')}
                  style={{ 
                    padding: "12px", 
                    textAlign: "left", 
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Last Calculation <span style={{ opacity: orderBy === 'lastEcobalyseAt' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'lastEcobalyseAt' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th
                  onClick={() => handleSort('ecobalyseVersion')}
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Version <span style={{ opacity: orderBy === 'ecobalyseVersion' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'ecobalyseVersion' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
                <th
                  onClick={() => handleSort('ecobalyseResultType')}
                  style={{
                    padding: "12px",
                    textAlign: "left",
                    borderBottom: "2px solid #ddd",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap"
                  }}
                >
                  Result Type <span style={{ opacity: orderBy === 'ecobalyseResultType' ? 1 : 0.3, display: 'inline-block', width: '12px', textAlign: 'center' }}>
                    {orderBy === 'ecobalyseResultType' ? (order === 'asc' ? '↑' : '↓') : '↑↓'}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {getSortedData(filteredGtins)
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((gtin) => (
                  <tr
                    key={gtin.id}
                    onClick={(e) => handleRowClick(gtin, e)}
                    style={{
                      borderBottom: "1px solid #ddd",
                      cursor: gtin._hasRealId ? "pointer" : "not-allowed",
                      transition: "background-color 0.2s",
                      opacity: gtin._hasRealId ? 1 : 0.6,
                      backgroundColor: selectedGtins.includes(gtin.id) ? "#f5f5f5" : "transparent"
                    }}
                    onMouseEnter={(e) => {
                      if (gtin._hasRealId && !selectedGtins.includes(gtin.id)) {
                        e.currentTarget.style.backgroundColor = "#fafafa";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedGtins.includes(gtin.id)) {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={selectedGtins.includes(gtin.id)}
                        onChange={() => handleSelectOne(gtin.id)}
                        disabled={!gtin._hasRealId}
                        onClick={(e) => e.stopPropagation()}
                        style={{ cursor: gtin._hasRealId ? "pointer" : "not-allowed" }}
                      />
                    </td>
                    <td style={{ padding: "12px" }}>{gtin.name || "-"}</td>
                    <td style={{ padding: "12px" }}>{gtin.articleId || "-"}</td>
                    <td style={{ padding: "12px" }}>{gtin.reference || "-"}</td>
                    <td style={{ padding: "12px" }}>{gtin.gtin || "-"}</td>
                    <td style={{ padding: "12px" }}>{gtin.projectName || "-"}</td>
                    <td style={{ padding: "12px" }}>{gtin.traceabilityStatus || "-"}</td>
                    <td style={{ padding: "12px" }}>{gtin.EcobalyseStatus || "-"}</td>
                    <td
                      title={naReasonFor(gtin) || undefined}
                      style={{
                        padding: "12px",
                        maxWidth: "320px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {naReasonFor(gtin) || "-"}
                    </td>
                    <td style={{ padding: "12px" }}>{formatDate(gtin.lastEcobalyseAt)}</td>
                    <td style={{ padding: "12px" }}>{gtin.ecobalyseVersion || "-"}</td>
                    <td style={{ padding: "12px" }}>{gtin.ecobalyseResultType || "-"}</td>
                  </tr>
                ))}
            </tbody>
          </table>

          <TablePagination
            component="div"
            count={filteredGtins.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </div>
      )}

      <Confirm
        isOpen={confirmDeleteOpen}
        title="Eliminar GTINs"
        content={`¿Está seguro que desea eliminar ${selectedGtins.length} GTIN${selectedGtins.length !== 1 ? 's' : ''}? Esta acción no se puede deshacer.`}
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
      />
    </div>
  );
};

export default GtinListTab;
